import React from 'react';
import { useParams, useLocation, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageSeo } from '../seo/PageSeo';
import { blogPostingSchema, breadcrumbSchema } from '../seo/schemas';
import { localePrefixedPath } from '../seo/constants';
import { findArticle } from './articles';
import { formatArticleDate } from './formatArticleDate';
import { BlogIndexMessages as M, BlogIndexText } from './BlogIndexPage.messages';
import { CommonMessages } from '../../i18n/Common.messages';
import { Footer } from '../homepage/Footer';
import './blog.css';

export const BlogArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  if (!slug) return <Navigate to="/blog" replace />;
  const article = findArticle(slug);
  if (!article) return <Navigate to="/blog" replace />;
  const fm = article.module.frontmatter;
  const Content = article.module.default;

  return (
    <>
      <PageSeo
        path={`/blog/${slug}`}
        titleKey={fm.title}
        descriptionKey={fm.description}
        ogImage={fm.ogImage || '/og/og-default.png'}
        ogType="article"
        publishedTime={fm.date}
        author={fm.author}
        jsonLd={[
          blogPostingSchema({
            title: fm.title,
            description: fm.description,
            date: fm.date,
            author: fm.author,
            slug,
            ogImage: fm.ogImage,
          }),
          breadcrumbSchema([
            { name: t(CommonMessages.BREADCRUMB_HOME), path: '/' },
            { name: t(M.BREADCRUMB, { defaultValue: BlogIndexText.BREADCRUMB }), path: '/blog' },
            { name: fm.title, path: `/blog/${slug}` },
          ]),
        ]}
      />
      <div className="page-wrapper">
        <article className="blog-article">
          {/* There was no way back to the index except the browser button. */}
          <Link to={localePrefixedPath('/blog', pathname)} className="blog-back">
            <span aria-hidden="true">←</span>
            {t(M.BACK_TO_BLOG, { defaultValue: BlogIndexText.BACK_TO_BLOG })}
          </Link>

          <header className="blog-article-header">
            {fm.tags && fm.tags.length > 0 && (
              <div className="blog-tags">
                {fm.tags.slice(0, 4).map(tag => (
                  <span key={tag} className="blog-tag">{tag}</span>
                ))}
              </div>
            )}
            <h1>{fm.title}</h1>
            {/*
              The description already exists in frontmatter and was only ever used for
              meta tags. As a lede it earns its keep twice.
            */}
            {fm.description && <p className="blog-article-lede">{fm.description}</p>}
            <div className="blog-article-meta">
              <time dateTime={fm.date}>{formatArticleDate(fm.date, i18n.language)}</time>
              <span className="blog-card-meta-sep" aria-hidden="true">·</span>
              <span>{fm.author}</span>
            </div>
          </header>

          {/* Scoped wrapper: MDX emits bare tags, and styling them globally would
              reach the header and footer too. */}
          <div className="blog-prose">
            <Content />
          </div>

          {/* Every article is an entry point from search. Leaving one without a next
              step wastes the visit. */}
          <aside className="blog-cta">
            <p className="blog-cta-title">{t(M.CTA_TITLE, { defaultValue: BlogIndexText.CTA_TITLE })}</p>
            <p className="blog-cta-text">{t(M.CTA_TEXT, { defaultValue: BlogIndexText.CTA_TEXT })}</p>
            <Link to={localePrefixedPath('/get-started', pathname)} className="btn btn-primary">
              {t(M.CTA_BUTTON, { defaultValue: BlogIndexText.CTA_BUTTON })}
            </Link>
          </aside>
        </article>
        <Footer />
      </div>
    </>
  );
};
